import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarClock, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useDecideApproval } from '../../../hooks/mutations';
import CadenceEditor from '../CadenceEditor';
import { cadenceText, formatRun } from '../cadence';
import {
  CardRow, CardShell, ResolvedCard, dangerBtn, ghostBtn, inputCls, outcomeOf, parseResult, primaryBtn, resultError, secondaryBtn,
} from './CardShell';

const Instruction = ({ text }) => {
  const { t } = useTranslation();
  const [all, setAll] = useState(false);
  const long = (text || '').length > 180;
  return (
    <div className="mt-1.5 rounded-lg bg-muted/60 px-3 py-2 text-[13px] text-foreground/85 whitespace-pre-wrap break-words">
      {long && !all ? `${text.slice(0, 180)}...` : text}
      {long && (
        <button type="button" onClick={() => setAll((v) => !v)} className="ml-1 text-xs text-amber-700 dark:text-amber-400 hover:underline">
          {t(all ? 'bots.cards.show_less' : 'bots.cards.show_more')}
        </button>
      )}
    </div>
  );
};

/**
 * The bot wants to plan (or change) its scheduled work: when, what it will do,
 * the next runs. The person confirms, edits first, or says no.
 */
const ScheduleCard = ({ approval, botName, compact }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const decide = useDecideApproval();
  const card = approval.card;
  const tz = card.timezone;
  const changes = card.changes || {};
  const shown = { title: changes.title || card.title, instruction: changes.instruction || card.instruction,
    cadence: changes.cadence || card.cadence, skills: changes.skills || card.skills || [] };
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(shown);

  if (approval.status !== 'pending') {
    const done = approval.status === 'approved';
    const error = resultError(approval);
    const made = parseResult(approval);
    const final = { ...shown, ...(approval.edits || {}) };
    const scheduleId = made?.scheduled?.schedule_id || made?.updated?.schedule_id || card.schedule_id;
    return (
      <ResolvedCard icon={CalendarClock} testid="schedule-card-resolved" ok={done && !error}
        text={`${final.title} · ${cadenceText(t, final.cadence, lang)}`}
        outcome={error ? t('bots.cards.failed') : outcomeOf(approval, t, t(`bots.cards.schedule_done_${card.action}`))}
        action={done && !error && card.action !== 'delete' && scheduleId ? (
          <Link to={`/app/bots/scheduled?id=${scheduleId}`} className="flex-shrink-0 p-1 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
            title={t('bots.cards.open_schedule')}>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        ) : null} />
    );
  }

  const busy = decide.isPending;
  const send = (approve) => {
    const edits = editing && approve ? {
      ...(draft.title !== shown.title ? { title: draft.title } : {}),
      ...(draft.instruction !== shown.instruction ? { instruction: draft.instruction } : {}),
      ...(JSON.stringify(draft.cadence) !== JSON.stringify(shown.cadence) ? { cadence: draft.cadence } : {}),
    } : undefined;
    decide.mutate({ approvalId: approval.id, approve, edits: edits && Object.keys(edits).length ? edits : undefined }, {
      onError: (err) => toast.error(err?.response?.data?.detail || t('bots.cards.failed')),
    });
  };
  const action = card.action;
  const isDelete = action === 'delete';
  const confirmLabel = t(`bots.cards.schedule_confirm_${action}`);

  return (
    <CardShell icon={CalendarClock} testid="schedule-card" waiting compact={compact}
      eyebrow={t(`bots.cards.schedule_eyebrow_${action}`, { bot: botName })}
      title={editing ? null : shown.title}
      footer={(
        <>
          {!isDelete && action !== 'run_now' && (action === 'create' || action === 'update') && !editing && (
            <button type="button" className={secondaryBtn} onClick={() => { setDraft(shown); setEditing(true); }} disabled={busy}
              data-testid="schedule-card-edit">
              <Pencil className="w-3.5 h-3.5" />{t('bots.cards.edit')}
            </button>
          )}
          <span className="flex-1" />
          <button type="button" className={ghostBtn} onClick={() => send(false)} disabled={busy} data-testid="card-decline">
            {t(isDelete ? 'bots.cards.keep' : 'bots.cards.not_now')}
          </button>
          <button type="button" className={isDelete ? dangerBtn : primaryBtn} onClick={() => send(true)}
            disabled={busy || (editing && (!draft.title.trim() || !draft.instruction.trim()))} data-testid="card-confirm">
            {confirmLabel}
          </button>
        </>
      )}>
      {editing ? (
        <div className="space-y-2.5 pb-1">
          <input value={draft.title} maxLength={80} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
            className={inputCls} aria-label={t('bots.schedules.field_title')} data-testid="schedule-card-title" />
          <textarea value={draft.instruction} maxLength={4000} rows={3}
            onChange={(e) => setDraft((d) => ({ ...d, instruction: e.target.value }))}
            className={`${inputCls} h-auto py-2 resize-y`} aria-label={t('bots.schedules.field_instruction')} />
          <CadenceEditor value={draft.cadence} timezone={tz} onChange={(cadence) => setDraft((d) => ({ ...d, cadence }))} />
        </div>
      ) : (
        <div>
          <CardRow label={t('bots.cards.when')}>
            {action === 'update' && changes.cadence ? (
              <span><span className="line-through text-muted-foreground mr-1.5">{cadenceText(t, card.cadence, lang)}</span>{cadenceText(t, changes.cadence, lang)}</span>
            ) : cadenceText(t, shown.cadence, lang)}
          </CardRow>
          {(card.next || []).length > 0 && (
            <CardRow label={t('bots.cards.next')}>
              <span className="flex flex-wrap gap-1">
                {card.next.map((n) => (
                  <span key={n} className="px-1.5 py-0.5 rounded-md bg-accent text-xs tabular-nums">{formatRun(n, lang, tz)}</span>
                ))}
              </span>
            </CardRow>
          )}
          {card.watch && (
            <CardRow label={t('bots.cards.watch')}>{t('bots.cards.watch_text', { tool: card.watch.tool })}</CardRow>
          )}
          {shown.skills.length > 0 && (
            <CardRow label={t('bots.cards.skills')}>
              <span className="flex flex-wrap gap-1">
                {shown.skills.map((s) => <span key={s} className="px-1.5 py-0.5 rounded-md border border-border text-xs">{s}</span>)}
              </span>
            </CardRow>
          )}
          {!isDelete && action !== 'run_now' && action !== 'resume' && <Instruction text={shown.instruction} />}
        </div>
      )}
    </CardShell>
  );
};

export default ScheduleCard;
